<script setup lang="ts">
/**
 * The user menu in the header (design prototype `#userMenu`, start version): who is signed in,
 * the role as a badge, the customer with its instance or the team (B-17, C-12), where the account
 * is managed (B-18: at dataCycle, ADR-046), a way to write to the team when the session may
 * (`inquiry.create`, AP-F6.5), the colour scheme, and the logout. Opens on click, closes on a
 * click outside and on Escape, which gives the focus back to the button. A disclosure, not a menu:
 * the button says open or closed (`aria-expanded`) and promises no arrow keys (`aria-haspopup`
 * would, GU4); Tab leads through the panel, which follows the button.
 */
import { computed, onBeforeUnmount, onMounted, ref, useId, useTemplateRef } from "vue";
import type { SessionResponse } from "@ai-platform/contracts";
import PixButton from "@pixelpoint-pattern-library-v2/base/components/styled/base/primitives/PixButton/PixButton.vue";
import ThemePicker from "./ThemePicker.vue";
import { useLogout } from "../composables/auth.ts";
import { CUSTOMER_KINDS, useInquiryDialog } from "../composables/inquiry.ts";
import { can } from "../session/access.ts";

const props = defineProps<{ session: SessionResponse }>();

const open = ref(false);
const root = useTemplateRef<HTMLElement>("root");
const trigger = useTemplateRef<HTMLButtonElement>("trigger");
const panelId = useId();
const { mutate: logout, isPending } = useLogout();

const name = computed(() => props.session.user.displayName ?? props.session.user.email);
const initials = computed(() =>
  name.value
    .split(/[\s.@_-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join(""),
);
/** The role as the prototype names it: "Administrator" of a customer, "Admin" of the team (B-17). */
const role = computed(() => {
  const { staffRole, membership } = props.session;
  if (staffRole) return staffRole === "admin" ? "Admin" : "Support";
  return membership?.role === "administrator" ? "Administrator" : "Anwender";
});
const context = computed(() => {
  const { customer } = props.session;
  return customer ? `${customer.name} · ${customer.instanceHost}` : "Team · pixelpoint dataCycle";
});
const accountNote = computed(() =>
  props.session.customer
    ? "Konto und Passwort verwaltet ihr in eurer dataCycle-Instanz."
    : "Konto und Passwort verwaltet ihr in der zentralen Instanz.",
);

const inquiry = useInquiryDialog();
const mayWrite = computed(() => can(props.session, "inquiry.create"));

function writeToTeam() {
  open.value = false;
  inquiry.open({ kinds: CUSTOMER_KINDS });
}

function onPointerDown(event: PointerEvent) {
  if (open.value && event.target instanceof Node && !root.value?.contains(event.target)) open.value = false;
}

/** Escape closes an open panel and gives the focus back to the button, wherever in the panel it was. */
function onKeyDown(event: KeyboardEvent) {
  if (event.key !== "Escape" || !open.value) return;
  open.value = false;
  trigger.value?.focus();
}

onMounted(() => {
  document.addEventListener("pointerdown", onPointerDown);
  document.addEventListener("keydown", onKeyDown);
});
onBeforeUnmount(() => {
  document.removeEventListener("pointerdown", onPointerDown);
  document.removeEventListener("keydown", onKeyDown);
});
</script>

<template>
  <div ref="root" class="relative">
    <button
      ref="trigger"
      type="button"
      class="rounded-pill text-text-head hover:bg-fill focus-visible:outline-focus inline-flex min-h-(--size-field) cursor-pointer items-center gap-2 border-0 bg-transparent py-1 pr-3 pl-1 focus-visible:outline-2 focus-visible:outline-offset-2"
      :aria-expanded="open"
      :aria-controls="panelId"
      data-user-menu
      @click="open = !open"
    >
      <span
        class="rounded-pill bg-accent-bg text-meta text-accent-text inline-flex size-8 items-center justify-center font-bold"
        aria-hidden="true"
      >
        {{ initials }}
      </span>
      <span class="text-ui hidden max-w-48 truncate font-semibold sm:inline">{{ name }}</span>
      <span class="sr-only sm:hidden">Benutzermenü</span>
    </button>

    <div
      v-show="open"
      :id="panelId"
      class="rounded-card border-line shadow-popover absolute top-full right-0 z-(--z-dropdown) mt-2 w-72 border bg-(--color-surface) p-4"
    >
      <p class="m-0 flex items-center gap-2">
        <span class="text-text-head min-w-0 truncate font-semibold">{{ name }}</span>
        <span
          class="rounded-chip border-accent-line bg-accent-bg text-label text-accent-text flex-none border px-2 py-0.5 font-semibold"
          data-role
        >
          {{ role }}
        </span>
      </p>
      <p class="text-meta text-text-soft m-0 truncate">{{ props.session.user.email }}</p>
      <p class="text-meta text-text-body mt-2 mb-0 truncate" data-context>{{ context }}</p>
      <p class="text-meta text-text-soft mt-1 mb-0">{{ accountNote }}</p>

      <div v-if="mayWrite" class="border-line mt-4 border-t pt-4">
        <PixButton
          theme="secondary"
          outline
          class="text-ui min-h-(--size-field) w-full font-semibold"
          data-action="write-to-team"
          @click="writeToTeam"
        >
          Anfrage an das Team
        </PixButton>
      </div>

      <div class="border-line mt-4 border-t pt-4">
        <p class="text-label text-text-faint mt-0 mb-2 font-semibold tracking-[0.08em] uppercase">Darstellung</p>
        <ThemePicker />
      </div>

      <div class="border-line mt-4 border-t pt-4">
        <PixButton
          outline
          class="text-ui min-h-(--size-field) w-full font-semibold"
          :disabled="isPending"
          @click="logout()"
        >
          Abmelden
        </PixButton>
      </div>
    </div>
  </div>
</template>
